import React, { ComponentProps, useEffect, useState } from 'react';
import { AxiosResponse } from 'axios';
import IForm from '../../types/IForm';
import { PageType } from '../../types/api/PageType';
import IUrl from '../../types/IUrl';
import { GlobalDocumentIds } from '../../types/GlobalDocumentIds';
import { Namespace } from '../../translation/namespace';
import PageState, { PageStateProps } from './partials/PageState';
import { Action, StaticDefaultView, Views } from '../../types/BlockWithViews';
import { accessKeys } from '../../types/AccessKeys';
import { PageManagementActionButton, PageManagementActionLink } from './partials/PageManagementActionButton';
import { Icons, SVGIcon } from '../../components/atoms/SVG';
import { useAppContext } from '../../contexts/AppContext/AppContext';
import { BADGE_TYPE, PageManagementBadge } from './partials/PageManagementBadge';
import Btn, { BtnModifier } from '../../components/molecules/Btn';
import { TooltipPlacement } from '../../components/atoms/Tooltip';
import ModalDialog from '../../components/organisms/ModalDialog/ModalDialog';
import YesNoForm from '../../components/forms/DeleteForm/YesNoForm';
import { AnnouncementCreateOrUpdate } from '../Announcements';
import { FormResponseErrorType } from '../../components/molecules/Form';
import PageMoveForm from './forms/PageMoveForm/PageMoveForm';
import SortChildPagesForm from './forms/SortChildPagesForm/SortChildPagesForm';
import { TopHeadlinesForm } from '../TopHeadlines';

import { ModalDialogModifier } from '../../components/organisms/ModalDialog/ModalDialog.modifier';
import { asBlock } from '../../_lib/BlockFetcher';
import { PageManagementCollapsibleActions } from './partials/PageManagementCollapsibleActions';
import { BlockType } from '../../types/api/ElementType';
import { BlockGuard } from '../../types/api/Block';
import { useSonTranslation } from '../../translation/useSonTranslation';
import { translatePageType } from '../../_lib/components/PageType/PageTypeTitle';
import { AttributeGroupCreateForm, AttributeGroupForm } from '../AttributeGroupInfo';
import { CreatePageForm, CreatePageSonForm } from './forms/CreatePageForm/CreatePageForm';
import i18n from '../../translation/i18n';
import { LinkingPageType } from '../../types/api/LinkingPageType';
import { ArchivePageForm } from './forms/ArchivePageForm/ArchivePageForm';
import { CreateEditPageMembershipMatchingRuleForm, DateParam, SonForm } from '../../_lib/Form2';
import { PublishForm, PublishSonForm } from '../../_lib/Form2/standardForms/Publish/PublishForm';
import useRedirect from '../../hooks/useRedirect';
import { useDelayed } from '../../hooks/useDelayed';
import {
  AnnouncementCreateOrUpdateForm,
} from '../Announcements/forms/AnnouncementCreateOrUpdate/AnnouncementCreateOrUpdate';
import {
  CreateEditUserToPageRoleForm,
} from '../../_lib/Form2/standardForms/CreateEditRoleForm/CreateEditUserToPageRoleForm';

type Prefixed<T extends string, TPrefix extends string> = T extends `${TPrefix}${infer _}` ? T : never;

function isPrefix<T extends string, TPrefix extends string>(
  action: T,
  prefix: TPrefix,
): action is Prefixed<T, TPrefix> {
  return action.startsWith(prefix);
}

export type PageManagementProps = Views<
  StaticDefaultView<{
    editModeToggle: IUrl;
    edit: IUrl;
    pageState: PageStateProps;
  }> &
  Action<'delete-page', { form: IForm; pageType: PageType; linkingPages?: LinkingPageType }> &
  Action<'publish', { form: PublishSonForm }> &
  Action<'propose-publication', { form: PublishSonForm }> &
  Action<'dePublish', { form: IForm; linkingPages?: LinkingPageType }> &
  Action<'restore', { form: IForm }> &
  Action<'create-announcementForPage', { form: AnnouncementCreateOrUpdateForm; pageType: PageType }> &
  Action<'create-announcement', { form: AnnouncementCreateOrUpdateForm; pageType: PageType }> &
  Action<'create-topheadline', { form: TopHeadlinesForm }> &
  Action<'create-page', { form: CreatePageSonForm }> &
  Action<'navigationsettings-move', { form: IForm }> &
  Action<'navigationsettings-sortchildpages', { form: IForm }> &
  Action<'navigationsettings-archive', { form: SonForm<DateParam<'archivedDate'>> }> &
  Action<'create-attributeGroup', { form: AttributeGroupCreateForm }> &
  Action<'delete-attributeGroup', { form: IForm }> &
  Action<'create-copy', { form: IForm }> &
  Action<'create-userToPageRole', { form: SonForm<any> }> &
  Action<'delete-userToPageRole', { form: IForm }> &
  Action<'create-userToUserRole', { form: SonForm<any> }> &
  Action<'delete-userToUserRole', { form: IForm }> &
  Action<'create-pageManagementMatchingRule',
    Pick<CreateEditPageMembershipMatchingRuleForm, 'form' | 'pageToRole' | 'areaSiteMapUrl'>> &
    Action<'delete-pageManagementMatchingRule', { form: IForm }>
>;

const CREATE_PREFIX = 'create';
const CREATE_PAGE_PREFIX = 'create-page';
const NAVIGATIONSETTINGS_PREFIX = 'navigationsettings';

/* if used correctly and wisely, accesskeys are a11y enhancement */

/* eslint-disable jsx-a11y/no-access-key */
export function PageManagement(props?: PageManagementProps | null) {
  type ModalViewTypes = Exclude<typeof activeView, undefined | 'default'>;

  const { t } = useSonTranslation('PageManagement');
  const [currentActiveView, setCurrentActiveView] = useState<ModalViewTypes | null>(null);
  const { userInfo, editModeInfo } = useAppContext();

  const [startRedirect] = useRedirect();
  const delayCloseModal = useDelayed((response: AxiosResponse) => {
    if (response.data.url) {
      startRedirect(response.data.url);
    } else {
      setCurrentActiveView(null);
    }
  });

  const accessKeyEnabled = userInfo?.settings?.accessKeysEnabled;

  // opening modals if activeView is in JSON.
  useEffect(() => {
    if (props?.activeView && props.activeView !== 'default') {
      setCurrentActiveView(props.activeView);
    }
  }, [props?.activeView]);

  if (!userInfo?.loggedIn || !props) {
    return null;
  }

  // needed for line 82 t be able to define ModalViewTypes #typscript magick :-)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { activeView, views } = props;

  /**
   * Needs to be refactored into separate forms.
   */
  const renderYesNoForm = (
    form: IForm,
    translationKeyPrefix:
      | 'views.delete-page'
      | 'views.delete-userToPageRole'
      | 'views.delete-userToUserRole'
      | 'views.dePublish'
      | 'views.restore'
      | 'views.delete-attributeGroup'
      | 'views.create-copy'
      | 'views.delete-pageManagementMatchingRule',
    errorStatuses: FormResponseErrorType[] = [],
    linkingPages: { fetchUrl: string } | undefined = undefined,
  ) => {
    const hintKey = `PageManagement:${translationKeyPrefix}.form.hint`;
    const formId = translationKeyPrefix.replace('views.', 'page-').concat('-form');

    return (
      <YesNoForm
        form={form}
        id={formId}
        hint={i18n.exists(hintKey) ? t(hintKey as any) : undefined}
        headline={t(`${Namespace.PageManagement}:${translationKeyPrefix}.form.question`)}
        transNamespaceAndKey={`${Namespace.PageManagement}:${translationKeyPrefix}`}
        cancelAction={() => setCurrentActiveView(null)}
        validateResponseStatus={status => (status >= 200 && status < 300) || status === 404}
        errorStatuses={errorStatuses}
        linkingPages={linkingPages}
      />
    );
  };

  const getModalDialogHeadline = (modalView: ModalViewTypes) => {
    if (modalView === 'delete-page') {
      return views['delete-page']
        ? t(`views.delete-page.title`, { pageType: translatePageType(views['delete-page'].embedded.pageType) })
        : '';
    }

    if (isPrefix(modalView, CREATE_PAGE_PREFIX) && modalView !== 'create-pageManagementMatchingRule') {
      return t(`views.create-page.title`);
    }
    return t(`views.${modalView}.title`);
  };

  /**
   * Mapping of what current action type to display.
   */
  const getFormByType = (action: ModalViewTypes) => {
    switch (action) {
      case 'create-attributeGroup': {
        const form = views[action]?.embedded?.form;
        return form && <AttributeGroupForm form={form} />;
      }
      case 'create-announcement':
      case 'create-announcementForPage': {
        const { form } = views[action]?.embedded || {};
        return form && <AnnouncementCreateOrUpdate type="create" form={form} />;
      }
      case 'delete-page': {
        const { form, pageType, linkingPages } = views[action]?.embedded || {};
        return (
          form &&
          pageType &&
          renderYesNoForm(form, `views.${action}`, [FormResponseErrorType.SUBPAGES_FAILED_TO_DEPUBLISH], linkingPages)
        );
      }
      case 'create-copy': {
        const form = views[action]?.embedded?.form;
        return form && renderYesNoForm(form, `views.${action}`, [FormResponseErrorType.PAGE_TYPE_IS_NOT_COPYABLE]);
      }
      case 'restore':
      case 'delete-attributeGroup': {
        const form = views[action]?.embedded?.form;
        return form && renderYesNoForm(form, `views.${action}`);
      }
      case 'dePublish': {
        const { form, linkingPages } = views[action]?.embedded || {};
        return (
          form &&
          renderYesNoForm(form, `views.${action}`, [FormResponseErrorType.SUBPAGES_FAILED_TO_DEPUBLISH], linkingPages)
        );
      }
      case 'navigationsettings-move': {
        const form = views[action]?.embedded?.form;
        return form && <PageMoveForm form={form} />;
      }
      case 'navigationsettings-sortchildpages': {
        const form = views[action]?.embedded?.form;
        return form && <SortChildPagesForm form={form} />;
      }
      case 'navigationsettings-archive': {
        const form = views[action]?.embedded?.form;
        return form && <ArchivePageForm form={form} />;
      }
      case 'publish':
      case 'propose-publication': {
        const form = views[action]?.embedded?.form;
        return (
          form && (
            <PublishForm
              form={form}
              onSuccess={delayCloseModal}
              customErrorTypeMessages={{
                personsNotPrivileged: t('views.propose-publication.form.status.personsNotPrivileged'),
              }}
            />
          )
        );
      }
      case 'create-topheadline': {
        const form = views[action]?.embedded?.form;
        return form && <TopHeadlinesForm form={form} type="create" />;
      }
      case 'create-page': {
        const form = views[action]?.embedded?.form;
        return form && <CreatePageForm form={form} />;
      }
      case 'create-userToUserRole':
      case 'create-userToPageRole': {
        const form = views[action]?.embedded?.form;
        return form && (
          <CreateEditUserToPageRoleForm
            form={form}
            onSuccess={delayCloseModal}
            elementType={action === 'create-userToPageRole'
              ? BlockType.usertopagerolepagelead
              : BlockType.usertouserrolepagelead}
            isEditing={false}
          />
        );
      }
      case 'delete-pageManagementMatchingRule':
      case 'delete-userToUserRole':
      case 'delete-userToPageRole': {
        const form = views[action]?.embedded?.form;
        return form && renderYesNoForm(form, `views.${action}`);
      }
      case 'create-pageManagementMatchingRule': {
        const { form, pageToRole, areaSiteMapUrl } = views[action]?.embedded || {};
        return (
          form &&
          pageToRole &&
          areaSiteMapUrl && (
            <CreateEditPageMembershipMatchingRuleForm
              form={form}
              pageToRole={pageToRole}
              areaSiteMapUrl={areaSiteMapUrl}
              onSuccess={delayCloseModal}
            />
          )
        );
      }
      default:
        return null;
    }
  };

  const getInitialFocusIndex = (action: ModalViewTypes) => {
    // focusing close button, otherwise link list could be overseen
    if (action === 'delete-page' || action === 'dePublish') {
      const { linkingPages } = views[action]?.embedded || {};
      if (linkingPages && Object.entries(linkingPages).length === 0) {
        return 0;
      }
      return 1;
    }
    switch (currentActiveView) {
      case 'navigationsettings-move':
      case 'create-topheadline':
      case 'create-page':
        return 2;
      default:
        return 1;
    }
  };

  /**
   * some special forms need special modifiers: here we return one for all multi step forms
   * @returns {null|ModalDialogModifier.WITH_NAVIGATION}
   */
  const getModalDialogModifier = () => {
    if (currentActiveView === 'create-topheadline' ||
      (currentActiveView?.startsWith(CREATE_PAGE_PREFIX) &&
        currentActiveView !== 'create-pageManagementMatchingRule')) {
      return [ModalDialogModifier.WITH_NAVIGATION];
    }

    if (currentActiveView === 'create-attributeGroup' || currentActiveView === 'create-pageManagementMatchingRule') {
      return [ModalDialogModifier.WIDE];
    }

    return null;
  };

  const renderActionLink = (viewType: ModalViewTypes) => {
    const view = views[viewType];
    return view
      ? (
          <PageManagementActionLink
            key={`page-management-action-${viewType}`}
            href={view.href}
            viewType={viewType}
            onClick={setCurrentActiveView}
          />
        )
      : null;
  };

  const renderDropDownMenu = (actionPrefix: string, icon: SVGIcon, title: string) => {
    const availableViewTypes = Object.keys(views) as ModalViewTypes[];
    const actions = availableViewTypes.filter(actionType => actionType.startsWith(actionPrefix));
    return (
      actions.length > 0 && (
        <PageManagementCollapsibleActions icon={icon} title={title}>
          {actions.map(renderActionLink)}
        </PageManagementCollapsibleActions>
      )
    );
  };

  return (
    <div
      className="pagemanagement"
      role="region"
      aria-labelledby={GlobalDocumentIds.pageManagementHeadline}
      id={GlobalDocumentIds.pageManagement}
    >
      <h2 className="invisible" id={GlobalDocumentIds.pageManagementHeadline}>
        {t(`${Namespace.PageManagement}:headline`)}
      </h2>
      {/* this table is never empty */}
      <ul className="pagemanagement__action-list">
        {views.default?.embedded?.pageState && (
          <li className="pagemanagement__button-container">
            <PageState {...views.default.embedded.pageState} />
          </li>
        )}
        {views.publish && (
          <PageManagementActionButton
            href={views.publish.href}
            icon={Icons.PUBLISH}
            viewType="publish"
            title={t('views.publish.title')}
            active={currentActiveView === 'publish'}
            onClick={setCurrentActiveView}
            accessKey={accessKeyEnabled ? accessKeys.publish : undefined}
          />
        )}
        {views['propose-publication'] && (
          <PageManagementActionButton
            href={views['propose-publication'].href}
            icon={Icons.PUBLISH}
            viewType="propose-publication"
            title={t('views.propose-publication.title')}
            active={currentActiveView === 'propose-publication'}
            onClick={setCurrentActiveView}
          />
        )}
        {views.dePublish && (
          <PageManagementActionButton
            href={views.dePublish.href}
            icon={Icons.DEPUBLISH}
            viewType="dePublish"
            title={t('views.dePublish.title')}
            active={currentActiveView === 'dePublish'}
            onClick={setCurrentActiveView}
            accessKey={accessKeyEnabled ? accessKeys.dePublish : undefined}
          />
        )}
        {views.restore && (
          <PageManagementActionButton
            href={views.restore.href}
            icon={Icons.RESTORE}
            onClick={setCurrentActiveView}
            active={currentActiveView === 'restore'}
            viewType="restore"
            title={t('views.restore.title')}
          />
        )}
        {editModeInfo.available && views.default?.embedded?.editModeToggle && (
          <li className="pagemanagement__button-container">
            {editModeInfo.active && (
              <div className="pagemanagement__button-badge">
                <PageManagementBadge ariaHidden badgeType={BADGE_TYPE.EDITMODE}>
                  {t('actions.editMode.text')}
                </PageManagementBadge>
              </div>
            )}
            <div className="pagemanagement__button-badge">
              <Btn
                href={views.default.embedded.editModeToggle.url || '/'}
                svgSpriteId={editModeInfo.active ? Icons.DECLINE_CROSS : Icons.EDIT_PENCIL}
                text={t(`actions.editMode.${editModeInfo.active ? 'closeText' : 'text'}`)}
                titleText={t(`actions.editMode.${editModeInfo.active ? 'closeTitle' : 'title'}`)}
                hiddenText
                modifier={editModeInfo.active ? BtnModifier.ACTIVE : undefined}
                tooltipPlacement={TooltipPlacement.TOP}
                accessKey={accessKeyEnabled ? accessKeys.editMode : undefined}
              />
            </div>
          </li>
        )}
        {renderDropDownMenu(NAVIGATIONSETTINGS_PREFIX, Icons.MOVE, t(`actions.pagenavigationsettings.text`))}
        {views['delete-page'] && (
          <PageManagementActionButton
            href={views['delete-page'].href}
            icon={Icons.DELETE_TRASHCAN}
            viewType="delete-page"
            title={t(`views.delete-page.title`, {
              pageType: translatePageType(views['delete-page'].embedded.pageType),
            })}
            active={currentActiveView === 'delete-page'}
            onClick={setCurrentActiveView}
            accessKey={accessKeyEnabled ? accessKeys.delete : undefined}
          />
        )}
        {views['delete-attributeGroup'] && (
          <PageManagementActionButton
            href={views['delete-attributeGroup'].href}
            icon={Icons.DELETE_TRASHCAN}
            viewType="delete-attributeGroup"
            title={t('views.delete-attributeGroup.title')}
            active={currentActiveView === 'delete-attributeGroup'}
            onClick={setCurrentActiveView}
            accessKey={accessKeyEnabled ? accessKeys.delete : undefined}
          />
        )}
        {views['delete-userToPageRole'] && (
          <PageManagementActionButton
            href={views['delete-userToPageRole'].href}
            icon={Icons.DELETE_TRASHCAN}
            viewType="delete-userToPageRole"
            title={t(`views.delete-userToPageRole.title`)}
            active={currentActiveView === 'delete-userToPageRole'}
            onClick={setCurrentActiveView}
            accessKey={accessKeyEnabled ? accessKeys.delete : undefined}
          />
        )}
        {views['delete-userToUserRole'] && (
          <PageManagementActionButton
            href={views['delete-userToUserRole'].href}
            icon={Icons.DELETE_TRASHCAN}
            viewType="delete-userToUserRole"
            title={t(`views.delete-userToUserRole.title`)}
            active={currentActiveView === 'delete-userToUserRole'}
            onClick={setCurrentActiveView}
            accessKey={accessKeyEnabled ? accessKeys.delete : undefined}
          />
        )}
        {views['delete-pageManagementMatchingRule'] && (
          <PageManagementActionButton
            href={views['delete-pageManagementMatchingRule'].href}
            icon={Icons.DELETE_TRASHCAN}
            viewType="delete-pageManagementMatchingRule"
            title={t(`views.delete-pageManagementMatchingRule.title`)}
            active={currentActiveView === 'delete-pageManagementMatchingRule'}
            onClick={setCurrentActiveView}
            accessKey={accessKeyEnabled ? accessKeys.delete : undefined}
          />
        )}
        {views.default?.embedded?.edit && (
          <li className="pagemanagement__button-container">
            <Btn
              href={views.default.embedded.edit.url}
              svgSpriteId={Icons.EDIT_PAGE}
              text={t('actions.edit.text')}
              titleText={t('actions.edit.title')}
              hiddenText
              tooltipPlacement={TooltipPlacement.TOP}
              routing={false}
              target="_blank"
            />
          </li>
        )}
        {renderDropDownMenu(CREATE_PREFIX, Icons.ADD_PLUS, t(`actions.create.text`))}
      </ul>
      {currentActiveView && views[currentActiveView] && (
        <ModalDialog
          closeModal={() => setCurrentActiveView(null)}
          headline={getModalDialogHeadline(currentActiveView)}
          initialFocusIndex={getInitialFocusIndex(currentActiveView)}
          modifier={getModalDialogModifier()}
        >
          {getFormByType(currentActiveView)}
        </ModalDialog>
      )}
    </div>
  );
}

export const PageManagementBlock = asBlock(PageManagement, BlockType.pagemanagement);
export type PageManagementBlock = ComponentProps<typeof PageManagementBlock>;
export const isPageManagementBlock = BlockGuard<PageManagementBlock>(BlockType.pagemanagement);
